
import React from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import NumericInput from "@/components/ui/NumericInput";
import type { MortgageData } from "../CalculatorTabs";
import { calculateMortgageDataLegacy } from "../utils/legacyCalculatorWrapper";
import { calculateFundingFee } from "../utils/fundingFeeCalculations"; 
import type { AutoCalculateOptions } from "../utils/types";
import { InfoIcon, AlertTriangle } from "lucide-react";
import { formatCurrency, parseCurrency } from "../utils/formatters";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import FundingFeeToggle from "./subcomponents/FundingFeeToggle";
import CreditScoreInput from "./fha/CreditScoreInput";
import CashReservesInput from "./fha/CashReservesInput";
import HousingPaymentInput from "./fha/HousingPaymentInput";
import CompensatingFactors from "./fha/CompensatingFactors";

interface FHALoanFieldsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
}

// Extend MortgageData for local use with the fields needed for FHA loans
interface ExtendedMortgageData extends MortgageData {
  cashReserves?: number;
  currentHousingPayment?: number;
  residualIncome?: boolean;
}

const FHALoanFields: React.FC<FHALoanFieldsProps> = ({ mortgageData, setMortgageData, autoCalculate }) => {
  // Cast the mortgageData to include the optional properties
  const data = mortgageData as ExtendedMortgageData;
  
  // Helper function to handle input change
  const handleInputChange = (field: string, value: any) => {
    setMortgageData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      return calculateMortgageDataLegacy(updatedData, calculateFundingFee, autoCalculate);
    });
  };

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h3 className="font-medium text-sm">FHA Loan Details</h3>
      
      <div className="bg-blue-50 p-3 rounded-md text-sm">
        <p className="font-medium text-blue-800">FHA Loan Requirements:</p>
        <ul className="list-disc list-inside text-blue-700 mt-1 space-y-1">
          <li>Minimum 3.5% down payment (credit score 580+)</li>
          <li>Minimum 10% down payment (credit score 500-579)</li>
          <li>Property must be primary residence</li>
          <li>Mortgage insurance required</li>
        </ul>
      </div>
      
      {/* FHA Funding Fee Toggle */}
      <FundingFeeToggle
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
        label="Roll Upfront MIP into Loan"
        tooltipContent="When selected, the FHA Upfront Mortgage Insurance Premium (UFMIP) will be added to your loan amount instead of being paid at closing. This will increase your loan amount and monthly payment, but reduce your out-of-pocket costs at closing."
      />

      {/* Credit Score Field */}
      <CreditScoreInput 
        creditScore={data.creditScore} 
        onChange={(value) => handleInputChange("creditScore", value)} 
      />
      
      {/* Cash Reserves Fields */}
      <CashReservesInput 
        cashReserves={data.cashReserves || 0} 
        onChange={(value) => handleInputChange("cashReserves", value)} 
      />
      
      {/* Current Housing Payment Field */}
      <HousingPaymentInput 
        currentHousingPayment={data.currentHousingPayment || 0} 
        onChange={(value) => handleInputChange("currentHousingPayment", value)} 
      />
      
      {/* Additional Compensating Factors */}
      <CompensatingFactors 
        residualIncome={data.residualIncome || false} 
        onChange={(checked) => handleInputChange("residualIncome", checked)} 
      />
    </div>
  );
};

export default FHALoanFields;
